import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Activity } from 'lucide-react';
import { homeApi } from '@/utils/api';
import { getAny, setCached } from '@/utils/cache';
import { useHapticFeedback } from '@/hooks/useHapticFeedback';
import { GramIcon } from '@/components/ui/GramIcon';
import { useTranslation } from 'react-i18next';

const POLL_MS = 15000;

interface GaugeStats {
    success?: boolean;
    totalUsers: number;
    online: { m5: number; m15: number; m60: number };
    buyers: number;
    sellers: number;
    buyVolume: number;
    sellVolume: number;
    newToday: number;
    history: number[];
    ts?: number;
}

const EMPTY: GaugeStats = {
    totalUsers: 0,
    online: { m5: 0, m15: 0, m60: 0 },
    buyers: 0,
    sellers: 0,
    buyVolume: 0,
    sellVolume: 0,
    newToday: 0,
    history: [],
};

// ─── iOS-style spring count-up ───
function useAnimatedNumber(target: number, duration = 800): number {
    const [display, setDisplay] = useState(target);
    const fromRef = useRef(target);

    useEffect(() => {
        const from = fromRef.current;
        const to = Number.isFinite(target) ? target : 0;
        if (from === to) return;
        fromRef.current = to;
        const start = performance.now();
        let raf = 0;
        const step = (now: number) => {
            const p = Math.min((now - start) / duration, 1);
            const eased = 1 - Math.pow(1 - p, 3);
            setDisplay(from + (to - from) * eased);
            if (p < 1) raf = requestAnimationFrame(step);
        };
        raf = requestAnimationFrame(step);
        return () => cancelAnimationFrame(raf);
    }, [target, duration]);

    return display;
}

const fmtNumber = (n: number) => n.toLocaleString('en-US', { maximumFractionDigits: 1 });

// ─── Glowing live dot ───
const LiveDot = () => (
    <span className="relative flex h-[5px] w-[5px] shrink-0">
        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#34C759] opacity-70" />
        <span className="relative inline-flex rounded-full h-[5px] w-[5px] bg-[#34C759] shadow-[0_0_6px_2px_rgba(52,199,89,0.5)]" />
    </span>
);

export const UserStatsGauge: React.FC = () => {
    const { impactOccurred } = useHapticFeedback();
    const { t } = useTranslation(['common', 'home']);
    const [stats, setStats] = useState<GaugeStats>(() => getAny<GaugeStats>('user_gauge_stats') || EMPTY);
    const [connected, setConnected] = useState(!!getAny('user_gauge_stats'));
    const [now, setNow] = useState(Date.now());

    useEffect(() => {
        let alive = true;

        const load = async () => {
            try {
                const res = await homeApi.getUserStats();
                const data = await res.json();
                if (!alive) return;
                if (data?.success) {
                    setStats(data);
                    setCached('user_gauge_stats', data);
                    setConnected(true);
                }
            } catch (err) {
                console.error('[UserStatsGauge] fetch failed:', err);
                if (alive) setConnected(false);
            }
        };

        load();
        const id = setInterval(load, POLL_MS);
        return () => { alive = false; clearInterval(id); };
    }, []);

    useEffect(() => {
        const id = setInterval(() => setNow(Date.now()), 1000);
        return () => clearInterval(id);
    }, []);

    const totalVolume = (stats.buyVolume || 0) + (stats.sellVolume || 0);
    const aTotal = useAnimatedNumber(totalVolume);

    const clock = new Date(now).toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: true,
    }).toUpperCase();

    // Bar chart (24h): reference-style activity wave, always full.
    const BASE_WAVE = [14, 18, 13, 22, 28, 34, 26, 38, 44, 30, 48, 40, 34, 30, 38, 46, 40, 34, 50, 58, 48, 64, 74, 86];
    const spark = stats.history && stats.history.length ? stats.history : [];
    const raw = spark.length
        ? spark.map((r, i) => (r > 0 ? Math.max(r * 2, BASE_WAVE[i]) : BASE_WAVE[i]))
        : BASE_WAVE;
    const maxV = Math.max(...raw, 1);

    const barColor = (i: number) => {
        if (i >= 16) return '#34C759';
        if (i >= 12) return '#6C5CE7';
        if (i >= 8) return '#E17055';
        if (i >= 3) return '#6C5CE7';
        return '#3A3A3A';
    };

    const handlePress = useCallback(() => impactOccurred?.('light'), [impactOccurred]);

    return (
        <div className="select-none flex-1 min-w-0 w-full" onPointerDown={handlePress}>
            <div
                className="relative w-full rounded-[20px] glass-strong border border-white/5"
                style={{ boxShadow: '0 14px 30px -18px rgba(0,0,0,0.75), inset 0 1px 0 rgba(255,255,255,0.06)' }}
            >
                {/* ── Glass sheen ── */}
                <div className="absolute inset-0 rounded-[20px] pointer-events-none" style={{ background: 'linear-gradient(180deg, rgba(255,255,255,0.035) 0%, rgba(255,255,255,0) 26%)' }} />

                {/* ── Glowing corner brackets ── */}
                <div className="absolute -top-6 -left-6 w-20 h-20 rounded-full pointer-events-none" style={{ background: 'radial-gradient(circle, rgba(255,255,255,0.10) 0%, rgba(255,255,255,0.03) 40%, transparent 65%)' }} />
                <div
                    className="absolute top-0 left-0 w-9 h-9 rounded-tl-[20px] pointer-events-none"
                    style={{ borderTop: '1.5px solid rgba(255,255,255,0.92)', borderLeft: '1.5px solid rgba(255,255,255,0.92)', filter: 'drop-shadow(0 0 4px rgba(255,255,255,0.5))' }}
                />
                <div className="absolute -bottom-6 -right-6 w-20 h-20 rounded-full pointer-events-none" style={{ background: 'radial-gradient(circle, rgba(255,255,255,0.08) 0%, rgba(255,255,255,0.02) 40%, transparent 65%)' }} />
                <div
                    className="absolute bottom-0 right-0 w-9 h-9 rounded-br-[20px] pointer-events-none"
                    style={{ borderBottom: '1.5px solid rgba(255,255,255,0.92)', borderRight: '1.5px solid rgba(255,255,255,0.92)', filter: 'drop-shadow(0 0 4px rgba(255,255,255,0.5))' }}
                />

                {/* ── Content ── */}
                <div className="relative z-10 flex flex-col items-center px-3 pt-3.5 pb-2.5 portals-font">
                    {/* header */}
                    <div className="relative w-full flex items-center justify-center">
                        <div className="flex items-center gap-1.5">
                            <LiveDot />
                            <span className={`text-[9px] tracking-wide ${connected ? 'text-[#707070]' : 'text-[#555]'}`}>
                                MARKET {clock}
                            </span>
                        </div>
                        <Activity className="absolute right-0 top-0 w-3 h-3 text-[#707070]" strokeWidth={1.75} />
                    </div>

                    {/* main: total market volume (buy + sell, in GRAM) */}
                    <div className="mt-2 flex items-end justify-center">
                        <span className="portals-numeric text-[30px] leading-none font-extralight text-[#E0E0E0] tracking-tight">
                            {fmtNumber(aTotal)}
                        </span>
                        <GramIcon className="w-3 h-3 ml-1 mb-[5px]" />
                    </div>
                    <span className="mt-1 text-[8px] font-semibold uppercase tracking-[0.2em] text-[#555]">
                        Market volume
                    </span>

                    {/* buy / sell (real market stats) */}
                    <div className="mt-2.5 flex items-start justify-center gap-6">
                        <div className="flex flex-col items-center gap-0.5">
                            <div className="flex items-center gap-1">
                                <span className="w-1 h-1 rounded-full bg-[#34C759]" />
                                <span className="text-[8px] font-semibold text-[#707070] tracking-[0.16em]">{String(t('common:buy', 'BUY'))}</span>
                            </div>
                            <span className="portals-numeric text-[11px] font-normal text-[#E0E0E0]">{fmtNumber(stats.buyVolume || 0)}</span>
                            <span className="text-[7px] text-[#555]">{stats.buyers || 0} buyers</span>
                        </div>
                        <div className="flex flex-col items-center gap-0.5">
                            <div className="flex items-center gap-1">
                                <span className="w-1 h-1 rounded-full bg-[#E17055]" />
                                <span className="text-[8px] font-semibold text-[#707070] tracking-[0.16em]">{String(t('common:sell', 'SELL'))}</span>
                            </div>
                            <span className="portals-numeric text-[11px] font-normal text-[#E0E0E0]">{fmtNumber(stats.sellVolume || 0)}</span>
                            <span className="text-[7px] text-[#555]">{stats.sellers || 0} sellers</span>
                        </div>
                    </div>

                    {/* historical chart (compact) */}
                    <div className="w-full mt-3">
                        <div className="flex items-end justify-between w-full" style={{ height: 30 }}>
                            {raw.map((v, i) => {
                                const h = Math.max(Math.round((v / maxV) * 30), 2);
                                return (
                                    <div
                                        key={i}
                                        className="w-[3.5px] rounded-[2px]"
                                        style={{
                                            height: h,
                                            backgroundColor: barColor(i),
                                            opacity: v > 0 ? 0.95 : 0.7,
                                            transition: 'height 0.7s cubic-bezier(.22,1,.36,1), background-color 0.5s ease',
                                        }}
                                    />
                                );
                            })}
                        </div>
                        {/* timeline */}
                        <div className="mt-1 flex items-center justify-between px-0.5">
                            {['12AM', '6AM', '12PM', '6PM'].map((t) => (
                                <span key={t} className="text-[6.5px] font-mono text-[#707070]">{t}</span>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default UserStatsGauge;